import ItemCard from "@affinity/components/ItemCard/ItemCard";
import { CardGridContainer } from "@affinity/components/ProductList/ProductListCardGrid";
import { SkeletonText } from "@affinity/components/SkeletonText";
import { toUKPrice, toUKUnitPrice } from "@affinity/constants/FormatHelpers";
import { toDate } from "@affinity/constants/FormatHelpers";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import {ErrorStrong} from "@affinity/constants/textFieldErrorStyle";
import Button from "@affinity/MobiusOverrides/Button/Button";
import { AffinityDelivery, PriceOptionViewModel } from "@affinity/Store/StoreTypes";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import React from "react";

type DeliveryCardProps = {
    badge: string;
    title: string;
    total: number;
    pricePerLitre: number;
    value: AffinityDelivery;
    isLoading?: Boolean;
    hasErrored: Boolean;
    onClick?: Function;
};

type DeliveryCardsProps = {
    deliveryCheapest: PriceOptionViewModel|undefined,
    deliverySoonest: PriceOptionViewModel|undefined,
    errorMessage : string,
    isLoading: boolean,
    onClickCheapest?: Function
    onClickSoonest?: Function
};

const DeliveryCard: React.FC<DeliveryCardProps> = ({
    badge,
    title,
    total,
    pricePerLitre,
    value,
    isLoading,
    hasErrored,
    onClick,
}) => {
    return (
        <ItemCard.Container>
            <ItemCard.Badge>{badge}</ItemCard.Badge>
            {!onClick ? (
                !hasErrored && (
                    <Radio value={value.toString()}>
                        <ItemCard.Title>{title}</ItemCard.Title>
                    </Radio>
                )
            ) : (
                !hasErrored && (
                    <ItemCard.Title>{title}</ItemCard.Title>
                )
            )}
            <ItemCard.Text>
                {!hasErrored && (
                    <>
                        Total{" "}
                        <SkeletonText isLoading={isLoading || !total}>
                            <strong>{toUKPrice(total)}</strong> inc VAT{" "}
                        </SkeletonText>
                        <div>
                            <SkeletonText isLoading={isLoading || !pricePerLitre}>
                                <small>({toUKUnitPrice(pricePerLitre)}/litre ex VAT)</small>
                            </SkeletonText>
                        </div>
                    </>
                )}
            </ItemCard.Text>
            {onClick && (
                <Button onClick={() => onClick(value)} variant="secondary" sizeVariant="medium" disabled={!!isLoading || (!total) || (!pricePerLitre)}>
                    Select
                </Button>
            )}
        </ItemCard.Container>
    );
};

export const DeliveryCards: React.FC<DeliveryCardsProps> = ({
    isLoading,
    deliveryCheapest,
    deliverySoonest,
    errorMessage,
    onClickCheapest,
    onClickSoonest
}) => {

    const OffscreenSpan = styleHelpers.offscreenSpan;

    const optionTitles = {
        [AffinityDelivery.Cheapest]: {
            title: `Delivery by ${toDate(deliveryCheapest?.deliveryBy)}`,
            badge: "Cheapest deal"
        },
        [AffinityDelivery.Fastest]: {
            title: `Delivery by ${toDate(deliverySoonest?.deliveryBy)}`,
            badge: "Get it sooner"
        },
    };

    const deliverCardCheapest:DeliveryCardProps = {
        badge: optionTitles[AffinityDelivery.Cheapest].badge,
        title: optionTitles[AffinityDelivery.Cheapest].title,
        value : AffinityDelivery.Cheapest,
        total : deliveryCheapest?.netCost || 0,
        pricePerLitre : deliveryCheapest?.netUnitPrice || 0,
        hasErrored: !!errorMessage,
        onClick: onClickCheapest,
        isLoading
    };
    const deliverCardSoonest:DeliveryCardProps = {
        badge: optionTitles[AffinityDelivery.Fastest].badge,
        title: optionTitles[AffinityDelivery.Fastest].title,
        value : AffinityDelivery.Fastest,
        total : deliverySoonest?.netCost || 0,
        pricePerLitre : deliverySoonest?.netUnitPrice || 0,
        hasErrored: !!errorMessage,
        onClick: onClickSoonest,
        isLoading
    };
    const errorCss = `
        grid-column: 1 / 3;
    `;

    return (
        <CardGridContainer>
            {isLoading && <OffscreenSpan>Updating prices, please wait</OffscreenSpan>}
            <DeliveryCard {...deliverCardCheapest}/>
            <DeliveryCard {...deliverCardSoonest}/>
            {errorMessage && (
                <ErrorStrong text={errorMessage} extendedCss={errorCss} />
            )}
        </CardGridContainer>
    );
};